/**
 * Crop Adjustment Panel
 * 
 * Displays under Media tab when conversion mode is active
 * Shows position and scale values for selected media item
 * User adjusts via mouse on VideoPlayer canvas
 */

import React from 'react';
import { MediaItem } from '../../../types';
import styles from './CropAdjustmentPanel.module.css';
import { useTranslation } from 'react-i18next';

interface CropAdjustmentPanelProps {
  isActive: boolean;
  selectedMediaItem: MediaItem | null;
  targetAspectRatio: string | null;
  onReset?: (itemId: string) => void;
}

const CropAdjustmentPanel: React.FC<CropAdjustmentPanelProps> = ({
  isActive,
  selectedMediaItem,
  targetAspectRatio,
  onReset
}) => {
  const { t } = useTranslation('videoEditor');
  
  if (!isActive) return null;

  const position = selectedMediaItem?.position || { x: 0, y: 0 };
  const scale = selectedMediaItem?.scale || { scaleX: 1, scaleY: 1 };

  const handleReset = () => {
    if (onReset && selectedMediaItem?.id) {
      onReset(selectedMediaItem.id);
    }
  };

  return (
    <div className={styles.panel}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.icon}>🎯</span>
          <span>{t('videoEditor.cropAdjustment.title')}</span>
        </div>
        <div className={styles.badge}>
          {t('videoEditor.cropAdjustment.convertingTo')} {targetAspectRatio}
        </div>
      </div>

      {/* Instructions */}
      <div className={styles.instructions}>
        {selectedMediaItem ? (
          <>
            <div className={styles.instructionTitle}>
              {t('videoEditor.cropAdjustment.instructionTitle')}
            </div>
            <ul className={styles.instructionList}>
              <li><strong>{t('videoEditor.cropAdjustment.drag')}</strong> {t('videoEditor.cropAdjustment.dragInstruction')}</li>
              <li><strong>{t('videoEditor.cropAdjustment.scroll')}</strong> {t('videoEditor.cropAdjustment.scrollInstruction')}</li>
            </ul>
          </>
        ) : (
          <div className={styles.noSelection}>
            {t('videoEditor.cropAdjustment.noSelection')}
          </div>
        )}
      </div>

      {/* Values Display */}
      {selectedMediaItem && (
        <>
          <div className={styles.divider} />
          
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{t('videoEditor.cropAdjustment.currentValues')}</div>
            
            {/* Position */}
            <div className={styles.valueGroup}>
              <div className={styles.valueLabel}>
                <span className={styles.valueIcon}>📍</span>
                {t('videoEditor.cropAdjustment.position')}
              </div>
              <div className={styles.values}>
                <div className={styles.valueItem}>
                  <span className={styles.valueName}>X:</span>
                  <span className={styles.valueNumber}>
                    {position.x > 0 ? '+' : ''}{position.x.toFixed(1)}%
                  </span>
                </div>
                <div className={styles.valueItem}>
                  <span className={styles.valueName}>Y:</span>
                  <span className={styles.valueNumber}>
                    {position.y > 0 ? '+' : ''}{position.y.toFixed(1)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Scale */}
            <div className={styles.valueGroup}>
              <div className={styles.valueLabel}>
                <span className={styles.valueIcon}>🔍</span>
                {t('videoEditor.cropAdjustment.zoom')}
              </div>
              <div className={styles.values}>
                <div className={styles.valueItem}>
                  <span className={styles.valueName}>{t('videoEditor.cropAdjustment.scale')}:</span>
                  <span className={styles.valueNumber}>
                    {(scale.scaleX * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Reset Button */}
          <div className={styles.divider} />
          <button
            className={styles.resetButton}
            onClick={handleReset}
          >
            {t('videoEditor.cropAdjustment.resetButton')}
          </button>
        </>
      )}

      {/* Hint */}
      <div className={styles.hint}>
        {t('videoEditor.cropAdjustment.hint')}
      </div>
    </div>
  );
};

export default CropAdjustmentPanel;
